/**
 * CaseStudyGallery Component
 *
 * Responsive image gallery for case study pages with lightbox viewing.
 * Displays project photos in a grid layout with click-to-expand functionality.
 *
 * Following CLAUDE.md:
 * - Client component for lightbox state management
 * - Styled with Tailwind CSS utilities
 * - Brand-agnostic (uses semantic design tokens)
 * - Responsive grid (4→3→2→1 columns)
 * - Keyboard navigation (arrow keys, Esc)
 */

"use client";

import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useState } from "react";
import { OptimisedImage } from "@/components/ui/optimised-image";
import { cn, hashString } from "@/lib/utils";
import type { GalleryItem } from "@/lib/validation/common";

export interface CaseStudyGalleryProps {
	gallery: GalleryItem[];
	heading?: string;
	description?: string;
	className?: string;
}

/**
 * CaseStudyGallery displays project photos in a responsive grid
 *
 * Features:
 * - Responsive grid layout (4→3→2→1 columns)
 * - Shared lightbox modal with navigation
 * - Prev/Next buttons to navigate between images
 * - Keyboard navigation (← → arrows, Esc to close)
 * - Image counters (e.g., "3 of 12")
 * - Optional titles displayed on hover
 */
export function CaseStudyGallery({
	gallery,
	heading,
	description,
	className,
}: CaseStudyGalleryProps) {
	const [open, setOpen] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	// Filter out items without images
	const validGallery = gallery?.filter((item) => item.image) ?? [];
	const totalImages = validGallery.length;
	const currentItem = validGallery[currentIndex];

	// Reset currentIndex if it's out of bounds after filtering
	useEffect(() => {
		if (currentIndex >= totalImages && totalImages > 0) {
			setCurrentIndex(0);
		}
	}, [totalImages, currentIndex]);

	const goToPrev = useCallback(() => {
		setCurrentIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
	}, [totalImages]);

	const goToNext = useCallback(() => {
		setCurrentIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
	}, [totalImages]);

	const openLightbox = useCallback((index: number) => {
		setCurrentIndex(index);
		setOpen(true);
	}, []);

	// Keyboard navigation
	useEffect(() => {
		if (!open) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft") {
				e.preventDefault();
				goToPrev();
			} else if (e.key === "ArrowRight") {
				e.preventDefault();
				goToNext();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [open, goToPrev, goToNext]);

	// Don't render if no valid gallery images
	if (totalImages === 0) {
		return null;
	}

	// Use centered fixed-width layout for 1-2 images, full responsive grid for 3+
	const gridClass =
		totalImages <= 2
			? "grid gap-4 justify-center grid-cols-[repeat(auto-fit,minmax(250px,300px))]"
			: "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

	return (
		<section className={cn("w-full", className)}>
			{/* Optional heading and description */}
			{(heading || description) && (
				<header className="mb-8 text-center">
					{heading && (
						<h2 className="text-3xl font-bold tracking-tight text-foreground">
							{heading}
						</h2>
					)}
					{description && (
						<p className="mt-3 text-lg text-muted-foreground">{description}</p>
					)}
				</header>
			)}

			{/* Responsive image grid */}
			<div className={gridClass}>
				{validGallery.map((item, index) => {
					const key = `${hashString(item.image?.url ?? "no-image")}-${index}`;
					const counter = `${index + 1} of ${totalImages}`;

					return (
						<button
							key={key}
							type="button"
							className="group relative h-64 w-full cursor-pointer overflow-hidden rounded-lg text-left"
							onClick={() => openLightbox(index)}
							aria-label={`View ${item.title || (item.image?.alternativeText ?? "image")}, image ${index + 1} of ${totalImages}`}
						>
							{/* Counter badge */}
							<span className="absolute right-3 top-3 z-10 rounded bg-black/70 px-2 py-1 text-xs font-semibold text-white">
								{counter}
							</span>

							{/* Thumbnail image */}
							<span className="relative block h-full w-full transition-transform group-hover:scale-105">
								{item.image && (
									<OptimisedImage
										src={item.image.url}
										alt={item.image.alternativeText}
										focalX={item.image.focalX}
										focalY={item.image.focalY}
										fill
										className="h-64 object-cover"
										sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
									/>
								)}
							</span>

							{/* Optional title overlay */}
							{item.title && (
								<span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-sm font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
									{item.title}
								</span>
							)}
						</button>
					);
				})}
			</div>

			{/* Shared Lightbox Modal */}
			<Dialog.Root open={open} onOpenChange={setOpen}>
				<Dialog.Portal>
					{/* Overlay */}
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

					{/* Content */}
					<Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center focus:outline-none">
						{/* Title (required for accessibility) */}
						<Dialog.Title className="sr-only">
							{currentItem?.title ||
								(currentItem?.image?.alternativeText ?? "Gallery image")}
						</Dialog.Title>

						{/* Close button */}
						<Dialog.Close className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-3 text-white opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
							<svg
								className="h-6 w-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
								aria-hidden="true"
							>
								<title>Close</title>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
							<span className="sr-only">Close</span>
						</Dialog.Close>

						{/* Previous button */}
						{totalImages > 1 && (
							<button
								type="button"
								onClick={goToPrev}
								className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-primary p-3 shadow-xl ring-2 ring-primary-foreground/20 transition-all hover:scale-110 hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
								aria-label="Previous image"
							>
								<svg
									className="h-6 w-6 text-primary-foreground"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={3}
										d="M15 19l-7-7 7-7"
									/>
								</svg>
							</button>
						)}

						{/* Next button */}
						{totalImages > 1 && (
							<button
								type="button"
								onClick={goToNext}
								className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-primary p-3 shadow-xl ring-2 ring-primary-foreground/20 transition-all hover:scale-110 hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
								aria-label="Next image"
							>
								<svg
									className="h-6 w-6 text-primary-foreground"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
									aria-hidden="true"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={3}
										d="M9 5l7 7-7 7"
									/>
								</svg>
							</button>
						)}

						{/* Full-size image */}
						<div className="relative flex max-h-[85vh] max-w-[85vw] items-center justify-center">
							{currentItem?.image && (
								<OptimisedImage
									src={currentItem.image.url}
									alt={currentItem.image.alternativeText}
									width={1920}
									height={1080}
									className="h-auto max-h-[85vh] w-auto max-w-[85vw] object-contain"
									priority
								/>
							)}
						</div>

						{/* Footer with title and counter */}
						<div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 text-center">
							{currentItem?.title && (
								<Dialog.Description className="text-sm text-white">
									{currentItem.title}
								</Dialog.Description>
							)}
							<div className="mt-1 text-xs text-white/70">
								{currentIndex + 1} of {totalImages} — Use ← → arrow keys to
								navigate
							</div>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			{/* Gallery stats */}
			{totalImages > 0 && (
				<p className="mt-6 text-center text-sm text-muted-foreground">
					{totalImages} {totalImages === 1 ? "photo" : "photos"}
				</p>
			)}
		</section>
	);
}
